import React, { useState } from "react";
import { useGlobalModal } from "../../components/use-modal/use-modal";
import { isStandalone } from "../standalone";
import { useTranslation } from "react-i18next";
import { IOSA2HSPrompt } from "../../modules/onboarding/components/a2hs/ios-a2hs-prompt";
import { confirm } from "../../components/modal/comfirm";
import { showPWAInstalledReminder } from "../../modules/onboarding/pages/setting/web-push-confirm.utils";
import { LOCAL_STORAGE_KEY } from "../local-storage";

// Interface for BeforeInstallPromptEvent (Android and PC A2HS)
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Device type enum
type DeviceType = "ios" | "android" | "pc";

// Browser type enum
type BrowserType = "chrome" | "edge" | "safari" | "other";

// Detect device and browser type
export const getDeviceAndBrowserType = (): {
  device: DeviceType;
  browser: BrowserType;
  supportsInstallPrompt: boolean;
} => {
  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // Browser detection
  let browser: BrowserType = "other";
  if (ua.includes("chrome") && !ua.includes("edg")) {
    browser = "chrome";
  } else if (ua.includes("edg")) {
    browser = "edge";
  } else if (ua.includes("safari") && !ua.includes("chrome")) {
    browser = "safari";
  }
  const supportsPWAInstall = () => {
    return (
      typeof (window as any).BeforeInstallPromptEvent !== "undefined" ||
      ("standalone" in navigator && (navigator as any).standalone)
    );
  };

  const supportsInstallPrompt = supportsPWAInstall();

  // Device detection
  if (/iphone|ipad|ipod/.test(ua) || (platform.includes("mac") && isTouchDevice)) {
    return { device: "ios", browser, supportsInstallPrompt };
  }
  if (/android/.test(ua)) {
    return { device: "android", browser, supportsInstallPrompt };
  }
  if (platform.includes("win") || platform.includes("linux") || platform.includes("mac")) {
    return { device: "pc", browser, supportsInstallPrompt };
  }

  return { device: "pc", browser, supportsInstallPrompt };
};

const { device: deviceType, browser, supportsInstallPrompt } = getDeviceAndBrowserType();
const isIOS = deviceType === "ios";
const isAndroid = deviceType === "android";
const isPC = deviceType === "pc";
// const isChromeOrEdge = browser === "chrome" || browser === "edge";

const isInPWAMode = isStandalone();

if ((isAndroid || isPC) && !isInPWAMode) {
  const handler = (e: Event) => {
    e.preventDefault();
    console.log(`[A2HS] beforeinstallprompt event fired on ${deviceType}`, e);
    (window as any)._deferredPrompt = e as BeforeInstallPromptEvent;
    (window as any)._beforeinstallpromptEventFired = true;
  };
  window.addEventListener("beforeinstallprompt", handler);
}

export const useA2HS = () => {
  const { t } = useTranslation();
  const modal = useGlobalModal();
  const [hasShownPrompt, setHasShownPrompt] = useState<boolean>(
    localStorage.getItem(LOCAL_STORAGE_KEY.A2HS_PROMPT_SHOWN) === "true",
  );

  // Check if app is already installed
  const isInPWAMode = isStandalone();

  // Trigger A2HS prompt
  const triggerA2HSPrompt = async (forceTrigger: boolean = false) => {
    // check (window as any)._beforeinstallpromptEventFired to avoid showing prompt at wrong time
    // await a promise to ensure the event is fired
    const maxChecks = 10;
    let checkCount = 0;

    // Check the status of _beforeinstallpromptEventFired
    await new Promise((resolve) => {
      const check = () => {
        if ((window as any)._beforeinstallpromptEventFired) {
          resolve(true);
        } else if (checkCount >= maxChecks) {
          console.log("[A2HS] Skipped: System already installed PWA or event not fired");
          resolve(false);
        } else {
          checkCount++;
          setTimeout(check, 100);
        }
      };
      check();
    });
    console.log(
      `[A2HS] Attempting to trigger prompt: deviceType=${deviceType}, browser=${browser}, forceTrigger=${forceTrigger},  deferredPrompt=${!!(
        window as any
      )._deferredPrompt}, isInPWAMode=${isInPWAMode}`,
    );

    // Skip if already installed
    if (isInPWAMode) {
      console.log("[A2HS] Skipped: App is already installed");
      return;
    }

    // Skip if prompt has been shown and not forced
    if (!forceTrigger && hasShownPrompt) {
      console.log("[A2HS] Skipped: Prompt already shown");
      return;
    }

    // Mark prompt as shown (only for non-forced triggers)
    if (!forceTrigger) {
      localStorage.setItem(LOCAL_STORAGE_KEY.A2HS_PROMPT_SHOWN, "true");
      setHasShownPrompt(true);
    }

    if (isAndroid) {
      // Android: Show different prompt based on installation status
      if ((window as any)._deferredPrompt) {
        // Not installed: Show install prompt
        confirm({
          title: t("reminder_title"),
          message: t("Common.create_home_screen_shortcut_prompt"),
          confirmButtonText: t("Common.allow"),
          cancelButtonText: t("Common.skip_for_now"),
          onConfirm: () => {
            console.log("[A2HS] Showing native Android prompt");
            (window as any)._deferredPrompt?.prompt();
            (window as any)._deferredPrompt?.userChoice.then((choiceResult: any) => {
              const outcome = choiceResult.outcome;
              console.log(`[A2HS] User ${outcome} A2HS prompt on ${deviceType}`);
            });
            modal.close();
          },
          onCancel: () => {
            console.log("[A2HS] User canceled Android A2HS prompt");
            modal.close();
          },
        });
      } else {
        // Installed: Show prompt to use PWA
        forceTrigger && showPWAInstalledReminder();
      }
    } else if (isIOS) {
      // iOS: Show custom modal
      console.log("[A2HS] Showing iOS custom prompt");
      modal.open(
        <IOSA2HSPrompt
          onClose={() => {
            console.log("[A2HS] iOS prompt closed");
            modal.close();
          }}
        />,
      );
    } else if (isPC && supportsInstallPrompt) {
      if ((window as any)._deferredPrompt) {
        // PC (Chrome/Edge): Show native prompt
        console.log("[A2HS] Showing native PC prompt");
        try {
          await (window as any)._deferredPrompt.prompt();
          const choiceResult = await (window as any)._deferredPrompt.userChoice;
          console.log(`[A2HS] User ${choiceResult.outcome} A2HS prompt on ${deviceType}`);
        } catch (error) {
          if (error instanceof DOMException && error.name === "NotAllowedError") {
            console.warn(`[A2HS] downgraded to custom prompt due to "NotAllowedError"`);
            confirm({
              title: t("reminder_title"),
              message: t("Common.create_home_screen_shortcut_prompt"),
              confirmButtonText: t("Common.allow"),
              cancelButtonText: t("Common.skip_for_now"),
              onConfirm: async () => {
                await (window as any)._deferredPrompt!.prompt();
                const choiceResult = await (window as any)._deferredPrompt.userChoice;
                console.log(`[A2HS] User ${choiceResult.outcome} A2HS prompt on ${deviceType}`);
                modal.close();
              },
              onCancel: () => {
                console.log("[A2HS] User canceled Android A2HS prompt");
                modal.close();
              },
            });
          }
        }
      } else {
        // Installed: Show prompt to use PWA
        forceTrigger && showPWAInstalledReminder();
      }
    } else {
      console.log(
        "[A2HS] No prompt available: deferredPrompt not initialized or unsupported device/browser",
      );
    }
  };

  return { triggerA2HSPrompt, isInPWAMode, deviceType, browser };
};
